import React, { useState } from "react";
import { Clock, CheckCircle, XCircle, ChevronDown } from "lucide-react";

/**
 * OvertimeApprovalButton
 *
 * Renders inline in the attendance table row.
 * Shows nothing when:
 *   - overtimeApprovalRequired is false  → system auto-approves, no button needed
 *   - record has no overtime minutes     → nothing to approve
 *   - viewType is 'employee'             → read-only badge instead
 *
 * Props
 *   record               – attendance row object
 *   theme                – "dark" | "light"
 *   isRTL                – boolean
 *   language             – "en" | "ar"
 *   viewType             – "admin" | "manager" | "employee"
 *   overtimeApprovalRequired – boolean from companyConfig
 *   onApprove            – (record) => Promise<void>
 *   onReject             – (record, reason) => Promise<void>
 */
const OvertimeApprovalButton = ({
    record,
    theme,
    isRTL,
    language,
    viewType,
    canApproveOvertime = true,
    overtimeApprovalRequired,
    onApprove,
    onReject,
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const [loading, setLoading] = useState(false);

    const isDark = theme === "dark";
    const isAr = language === "ar";
    const isEmployee = viewType === "employee";
    // approve_overtime feature gate resolved upstream (defaults true so other
    // callers keep their historical behavior); employees stay read-only.
    const canAct = (viewType === "admin" || viewType === "manager") && canApproveOvertime;

    // ── guard: nothing to show ─────────────────────────────────────────────────
    const overtimeMinutes = parseInt(record.overTime, 10) || 0;
    if (!overtimeApprovalRequired || overtimeMinutes <= 0) return null;

    // ── approval state from record ─────────────────────────────────────────────
    const approvalStatus = record.overallStatus || "pending";
    // ── labels ─────────────────────────────────────────────────────────────────
    const lbl = {
        pending: isAr ? "انتظار موافقة الإضافي" : "Overtime Pending",
        approved: isAr ? "إضافي معتمد" : "Overtime Approved",
        rejected: isAr ? "إضافي مرفوض" : "Overtime Rejected",
        approve: isAr ? "اعتماد" : "Approve",
        reject: isAr ? "رفض" : "Reject",
        hours: isAr ? "ساعة" : "h",
        minutes: isAr ? "دقيقة" : "m",
    };

    const formatOT = (mins) => {
        if (mins >= 60) {
            const h = Math.floor(mins / 60);
            const m = mins % 60;
            return m > 0 ? `${h}${lbl.hours} ${m}${lbl.minutes}` : `${h}${lbl.hours}`;
        }
        return `${mins}${lbl.minutes}`;
    };

    // ── palette per status ──────────────────────────────────────────────────────
    const palette = {
        pending: {
            bg: isDark ? "rgba(245,158,11,0.15)" : "rgba(254,243,199,0.9)",
            border: isDark ? "rgba(245,158,11,0.35)" : "rgba(245,158,11,0.4)",
            color: isDark ? "#fcd34d" : "#92400e",
            dot: "#f59e0b",
        },
        approved: {
            bg: isDark ? "rgba(16,185,129,0.15)" : "rgba(209,250,229,0.9)",
            border: isDark ? "rgba(16,185,129,0.35)" : "rgba(16,185,129,0.4)",
            color: isDark ? "#6ee7b7" : "#065f46",
            dot: "#10b981",
        },
        rejected: {
            bg: isDark ? "rgba(239,68,68,0.15)" : "rgba(254,226,226,0.9)",
            border: isDark ? "rgba(239,68,68,0.35)" : "rgba(239,68,68,0.4)",
            color: isDark ? "#fca5a5" : "#991b1b",
            dot: "#ef4444",
        },
    };

    const p = palette[approvalStatus] || palette.pending;

    const isWorkflowActive = Boolean(record.workflowInstanceId);
    const cannotActOnWorkflow = isWorkflowActive && !record.currentUserCanAct;

    // ── action handlers ────────────────────────────────────────────────────────
    const handleApprove = async () => {
        if (cannotActOnWorkflow) return;
        setLoading(true);
        setShowMenu(false);
        try { await onApprove(record); } finally { setLoading(false); }
    };

    const handleReject = async () => {
        if (cannotActOnWorkflow) return;
        // The backend requires a non-empty rejection reason. The table row has
        // no inline reason field, so prompt for one here; bail if the approver
        // cancels or leaves it blank rather than firing a doomed request.
        const reason = (typeof window !== "undefined" ? window.prompt(
            isAr ? "سبب رفض طلب الوقت الإضافي:" : "Reason for rejecting this overtime request:"
        ) : null);
        if (!reason || !reason.trim()) return;
        setLoading(true);
        setShowMenu(false);
        try { await onReject(record, reason.trim()); } finally { setLoading(false); }
    };

    // ── employee read-only badge ───────────────────────────────────────────────
    if (isEmployee) {
        const Icon = approvalStatus === "approved" ? CheckCircle
            : approvalStatus === "rejected" ? XCircle
                : Clock;
        return (
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 10px",
                    borderRadius: "20px",
                    fontSize: "11px",
                    fontWeight: "600",
                    background: p.bg,
                    border: `1px solid ${p.border}`,
                    color: p.color,
                    whiteSpace: "nowrap",
                }}
            >
                <Icon size={11} />
                {formatOT(overtimeMinutes)}
                <span style={{ opacity: 0.7, fontSize: "10px" }}>
                    · {approvalStatus === "approved" ? lbl.approved
                        : approvalStatus === "rejected" ? lbl.rejected
                            : lbl.pending}
                </span>
            </span>
        );
    }

    // ── already actioned — show status chip with re-action dropdown ───────────
    // ── pending / manager+admin interactive button ────────────────────────────
    return (
        <div style={{ position: "relative", display: "inline-block" }}>
            {/* Main chip */}
            <button
                type="button"
                disabled={loading}
                onClick={() => canAct && setShowMenu((v) => !v)}
                title={`${formatOT(overtimeMinutes)} overtime — ${approvalStatus}`}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "6px 10px",
                    borderRadius: "8px",
                    border: `1.5px solid ${p.border}`,
                    background: p.bg,
                    color: p.color,
                    cursor: canAct && !loading ? "pointer" : "default",
                    fontSize: "11px",
                    fontWeight: "700",
                    whiteSpace: "nowrap",
                    transition: "all 0.18s ease",
                    opacity: loading ? 0.6 : 1,
                    letterSpacing: "0.01em",
                }}
                onMouseEnter={(e) => {
                    if (canAct && !loading) {
                        e.currentTarget.style.filter = "brightness(1.1)";
                        e.currentTarget.style.transform = "translateY(-1px)";
                    }
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.filter = "none";
                    e.currentTarget.style.transform = "translateY(0)";
                }}
            >
                {/* Pulsing dot for pending */}
                {approvalStatus === "pending" && !loading && (
                    <span style={{ position: "relative", width: "8px", height: "8px", flexShrink: 0 }}>
                        <span style={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "50%",
                            background: p.dot,
                            animation: "ot-pulse 1.6s ease-in-out infinite",
                        }} />
                        <span style={{
                            position: "absolute",
                            inset: "1px",
                            borderRadius: "50%",
                            background: p.dot,
                        }} />
                    </span>
                )}
                {approvalStatus === "approved" && <CheckCircle size={12} />}
                {approvalStatus === "rejected" && <XCircle size={12} />}
                {loading && (
                    <span style={{
                        width: "10px", height: "10px", border: `2px solid ${p.color}`,
                        borderTopColor: "transparent", borderRadius: "50%",
                        animation: "ot-spin 0.7s linear infinite", flexShrink: 0,
                    }} />
                )}

                <Clock size={11} style={{ opacity: 0.75 }} />
                {formatOT(overtimeMinutes)}

                {canAct && (
                    <ChevronDown
                        size={11}
                        style={{
                            opacity: 0.7,
                            transform: showMenu ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s",
                        }}
                    />
                )}
            </button>

            {/* Dropdown menu */}
            {showMenu && canAct && (
                <>
                    {/* backdrop */}
                    <div
                        style={{ position: "fixed", inset: 0, zIndex: 999 }}
                        onClick={() => setShowMenu(false)}
                    />
                    <div
                        style={{
                            position: "absolute",
                            top: "calc(100% + 6px)",
                            [isRTL ? "right" : "left"]: 0,
                            zIndex: 1000,
                            minWidth: "180px",
                            borderRadius: "10px",
                            overflow: "hidden",
                            boxShadow: isDark
                                ? "0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.07)"
                                : "0 8px 32px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.07)",
                            background: isDark ? "#1e293b" : "#ffffff",
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            padding: "10px 14px 8px",
                            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"}`,
                            fontSize: "11px",
                            color: isDark ? "#94a3b8" : "#64748b",
                            fontWeight: "600",
                            letterSpacing: "0.05em",
                            textTransform: "uppercase",
                        }}>
                            {formatOT(overtimeMinutes)} · {isAr ? "موافقة الإضافي" : "Overtime Approval"}
                            <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "3px" }}>
                                <span style={{ fontSize: "10px", fontWeight: "500", textTransform: "none", letterSpacing: 0 }}>
                                    {isAr ? "الحالة:" : "Status:"}{" "}
                                    <span style={{
                                        color: approvalStatus === "approved" ? "#10b981"
                                            : approvalStatus === "rejected" ? "#ef4444"
                                                : "#f59e0b"
                                    }}>
                                        {approvalStatus}
                                    </span>
                                </span>
                                {cannotActOnWorkflow && (
                                    <span style={{ fontSize: "10px", fontWeight: "600", color: "#f59e0b", marginTop: "4px", display: "block" }}>
                                        ⚠️ {isAr ? "في انتظار دور موافقة آخر" : "Waiting for other approver"}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Approve */}
                        {approvalStatus !== "approved" && (
                            <button
                                type="button"
                                onClick={handleApprove}
                                disabled={cannotActOnWorkflow}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    width: "100%",
                                    padding: "11px 14px",
                                    border: "none",
                                    background: "transparent",
                                    cursor: cannotActOnWorkflow ? "not-allowed" : "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: cannotActOnWorkflow ? (isDark ? "#475569" : "#cbd5e1") : (isDark ? "#6ee7b7" : "#065f46"),
                                    textAlign: isRTL ? "right" : "left",
                                    transition: "background 0.15s",
                                    direction: isRTL ? "rtl" : "ltr",
                                    opacity: cannotActOnWorkflow ? 0.5 : 1,
                                }}
                                onMouseEnter={(e) => {
                                    if (cannotActOnWorkflow) return;
                                    e.currentTarget.style.background = isDark
                                        ? "rgba(16,185,129,0.12)" : "rgba(16,185,129,0.08)";
                                }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                            >
                                <CheckCircle size={15} />
                                {lbl.approve}
                            </button>
                        )}

                        {/* Reject */}
                        {approvalStatus !== "rejected" && (
                            <button
                                type="button"
                                onClick={handleReject}
                                disabled={cannotActOnWorkflow}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    width: "100%",
                                    padding: "11px 14px",
                                    border: "none",
                                    background: "transparent",
                                    cursor: cannotActOnWorkflow ? "not-allowed" : "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: cannotActOnWorkflow ? (isDark ? "#475569" : "#cbd5e1") : (isDark ? "#fca5a5" : "#991b1b"),
                                    textAlign: isRTL ? "right" : "left",
                                    transition: "background 0.15s",
                                    direction: isRTL ? "rtl" : "ltr",
                                    opacity: cannotActOnWorkflow ? 0.5 : 1,
                                }}
                                onMouseEnter={(e) => {
                                    if (cannotActOnWorkflow) return;
                                    e.currentTarget.style.background = isDark
                                        ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)";
                                }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                            >
                                <XCircle size={15} />
                                {lbl.reject}
                            </button>
                        )}
                    </div>
                </>
            )}

            {/* Keyframe styles injected once */}
            <style>{`
        @keyframes ot-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50%       { transform: scale(2.2); opacity: 0; }
        }
        @keyframes ot-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

export default OvertimeApprovalButton;